import { FunctionComponent, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import styles from './AppointmentsPaginator.module.scss';

interface AppointmentsPaginatorProps {
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    className?: string;
}

const getVisiblePages = (currentPage: number, totalPages: number): Array<number | 'ellipsis'> => {
    if (totalPages <= 1) {
        return [1];
    }

    if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 3) {
        return [1, 2, 3, 4, 'ellipsis', totalPages];
    }

    if (currentPage >= totalPages - 2) {
        return [1, 'ellipsis', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', totalPages];
};

export const AppointmentsPaginator: FunctionComponent<AppointmentsPaginatorProps> = props => {
    const visiblePages = useMemo(() => getVisiblePages(props.page, props.totalPages), [props.page, props.totalPages]);

    if (props.totalPages <= 1) {
        return null;
    }

    const goToPage = (page: number) => {
        if (page < 1 || page > props.totalPages || page === props.page) {
            return;
        }

        props.onPageChange(page);
    };

    return (
        <div className={clsx(styles.root, props.className)}>
            <button
                type="button"
                className={styles.arrowButton}
                disabled={props.page <= 1}
                onClick={() => goToPage(props.page - 1)}
                aria-label="Previous page"
            >
                <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            {visiblePages.map((item, index) =>
                item === 'ellipsis' ? (
                    <span
                        key={`ellipsis-${index}`}
                        className={styles.ellipsis}
                    >
                        ...
                    </span>
                ) : (
                    <button
                        key={item}
                        type="button"
                        className={clsx(styles.pageButton, {
                            [styles.active]: item === props.page
                        })}
                        onClick={() => goToPage(item)}
                        aria-label={`Page ${item}`}
                        aria-current={item === props.page ? 'page' : undefined}
                    >
                        {item}
                    </button>
                )
            )}

            <button
                type="button"
                className={styles.arrowButton}
                disabled={props.page >= props.totalPages}
                onClick={() => goToPage(props.page + 1)}
                aria-label="Next page"
            >
                <FontAwesomeIcon icon={faChevronRight} />
            </button>
        </div>
    );
};

export default AppointmentsPaginator;
